import React, { useState } from 'react';
import styled from 'styled-components';
import UnifiedOrderService from '../services/UnifiedOrderService';

// Styled Components
const CheckoutContainer = styled.div`
  background-color: #0a0a0a;
  border-radius: 12px;
  padding: 24px;
  color: white;
  min-height: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const CheckoutHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const CheckoutTitle = styled.h2`
  color: #D4AF37;
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: bold;
`;

const CheckoutSubtitle = styled.p`
  color: #ccc;
  margin: 0;
  font-size: 16px;
`;

const VendorSection = styled.div`
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  border: 2px solid #4b5563;
`;

const VendorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const VendorName = styled.h3`
  color: #D4AF37;
  margin: 0;
  font-size: 18px;
  font-weight: bold;
`;

const VendorPosSystem = styled.span`
  background: rgba(212, 175, 55, 0.2);
  color: #D4AF37;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
`;

const VendorItems = styled.div`
  margin-bottom: 16px;
`;

const VendorItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #4b5563;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemName = styled.span`
  color: #d1d5db;
  font-size: 14px;
`;

const ItemQuantity = styled.span`
  color: #9ca3af;
  font-size: 12px;
  margin: 0 8px;
`;

const ItemPrice = styled.span`
  color: #22c55e;
  font-size: 14px;
  font-weight: 500;
`;

const VendorTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 2px solid #D4AF37;
  font-weight: bold;
`;

const VendorTotalLabel = styled.span`
  color: #D4AF37;
  font-size: 16px;
`;

const VendorTotalAmount = styled.span`
  color: #D4AF37;
  font-size: 18px;
`;

const OrderSummary = styled.div`
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  border: 2px solid #D4AF37;
`;

const SummaryTitle = styled.h4`
  color: #D4AF37;
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: bold;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
    padding-top: 16px;
    border-top: 2px solid #4b5563;
  }
`;

const SummaryLabel = styled.span`
  color: #d1d5db;
  font-size: 16px;
`;

const SummaryValue = styled.span`
  color: ${props => props.$highlight ? '#D4AF37' : '#22c55e'};
  font-size: ${props => props.$highlight ? '24px' : '16px'};
  font-weight: ${props => props.$highlight ? 'bold' : '500'};
`;

const PaymentSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  color: #D4AF37;
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: bold;
`;

const PaymentOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const PaymentOption = styled.div`
  padding: 16px;
  background: ${props => props.$selected ? 'linear-gradient(135deg, #D4AF37 0%, #fbbf24 100%)' : '#1f2937'};
  color: ${props => props.$selected ? '#0a0a0a' : 'white'};
  border: 2px solid ${props => props.$selected ? '#D4AF37' : '#4b5563'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  font-weight: 500;
  
  &:hover {
    border-color: #D4AF37;
    transform: translateY(-2px);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseButton = styled(Button)`
  background: transparent;
  color: #9ca3af;
  border: 2px solid #4b5563;
  
  &:hover {
    border-color: #6b7280;
    color: #d1d5db;
  }
`;

const ProcessButton = styled(Button)`
  background: linear-gradient(135deg, #D4AF37 0%, #fbbf24 100%);
  color: #0a0a0a;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(212, 175, 55, 0.3);
  }
`;

const ProcessingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ProcessingContent = styled.div`
  background: #1a1a1a;
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  border: 2px solid #D4AF37;
`;

const ProcessingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #333;
  border-top: 4px solid #D4AF37;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
`;

const ProcessingText = styled.h3`
  color: #D4AF37;
  margin: 0 0 10px 0;
  font-size: 20px;
`;

const ProcessingSubtext = styled.p`
  color: #ccc;
  margin: 0;
  font-size: 14px;
`;

const UnifiedCheckout = ({ cart, onClose, onSuccess }) => {
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');

  // Group cart items by vendor
  const groupedCart = cart.reduce((acc, item) => {
    const vendorId = item.vendorId || 'unknown';
    if (!acc[vendorId]) {
      acc[vendorId] = {
        vendorId: item.vendorId,
        vendorName: item.vendorName || 'Unknown Vendor',
        vendorPosSystem: item.vendorPosSystem || 'unknown',
        items: []
      };
    }
    acc[vendorId].items.push(item);
    return acc;
  }, {});

  const vendorGroups = Object.values(groupedCart);

  // Calculate totals
  const calculateVendorTotal = (items) => {
    return items.reduce((total, item) => total + (parseFloat(item.price) * item.quantity), 0);
  };

  const calculateGrandTotal = () => {
    return vendorGroups.reduce((total, vendor) => total + calculateVendorTotal(vendor.items), 0);
  };

  const calculateTax = () => {
    return calculateGrandTotal() * 0.08; // 8% tax
  };

  const calculateFinalTotal = () => {
    return calculateGrandTotal() + calculateTax();
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  const handleProcessOrder = async () => {
    setIsProcessing(true);
    
    try {
      // Prepare order data
      const vendorOrders = vendorGroups.map(vendor => ({
        vendorId: vendor.vendorId,
        vendorName: vendor.vendorName,
        vendorPosSystem: vendor.vendorPosSystem,
        items: vendor.items,
        total: calculateVendorTotal(vendor.items)
      }));

      const orderData = {
        vendorOrders: vendorOrders,
        paymentMethod: paymentMethod,
        totalAmount: calculateFinalTotal(),
        customerInfo: {
          // In a real app, this would come from a form
          name: 'Customer',
          email: 'customer@example.com',
          phone: '555-1234'
        }
      };

      // Step 1: Process unified order
      setProcessingStep('Processing unified order...');
      const result = await UnifiedOrderService.processUnifiedOrder(orderData);
      
      // Step 2: Show success
      setProcessingStep('Order completed successfully!');
      await new Promise(resolve => setTimeout(resolve, 1000));

      onSuccess({
        orderId: result.unifiedOrderId,
        vendorOrders: result.vendorResults,
        total: result.totalAmount,
        paymentMethod: paymentMethod,
        successfulVendors: result.vendorResults.filter(r => r.success).length,
        failedVendors: result.vendorResults.filter(r => !r.success).length
      });

    } catch (error) {
      console.error('Error processing order:', error);
      setProcessingStep('Error processing order. Please try again.');
      await new Promise(resolve => setTimeout(resolve, 2000));
    } finally {
      setIsProcessing(false);
    }
  };

  if (isProcessing) {
    return (
      <ProcessingOverlay>
        <ProcessingContent>
          <ProcessingSpinner />
          <ProcessingText>Processing Your Order</ProcessingText>
          <ProcessingSubtext>{processingStep}</ProcessingSubtext>
        </ProcessingContent>
      </ProcessingOverlay>
    );
  }

  return (
    <CheckoutContainer>
      <CheckoutHeader>
        <CheckoutTitle>🛒 Unified Checkout</CheckoutTitle>
        <CheckoutSubtitle>
          Pay once, we'll handle the rest with each vendor
        </CheckoutSubtitle>
      </CheckoutHeader>

      {/* Vendor Orders */}
      {vendorGroups.map((vendor, index) => (
        <VendorSection key={vendor.vendorId || index}>
          <VendorHeader>
            <VendorName>{vendor.vendorName}</VendorName>
            <VendorPosSystem>{vendor.vendorPosSystem}</VendorPosSystem>
          </VendorHeader>
          
          <VendorItems>
            {vendor.items.map((item, itemIndex) => (
              <VendorItem key={itemIndex}>
                <ItemName>{item.name}</ItemName>
                <ItemQuantity>x{item.quantity}</ItemQuantity>
                <ItemPrice>{formatPrice(parseFloat(item.price) * item.quantity)}</ItemPrice>
              </VendorItem>
            ))}
          </VendorItems>
          
          <VendorTotal>
            <VendorTotalLabel>Vendor Total:</VendorTotalLabel>
            <VendorTotalAmount>{formatPrice(calculateVendorTotal(vendor.items))}</VendorTotalAmount>
          </VendorTotal>
        </VendorSection>
      ))}

      {/* Order Summary */}
      <OrderSummary>
        <SummaryTitle>📋 Order Summary</SummaryTitle>
        
        <SummaryRow>
          <SummaryLabel>Subtotal ({cart.length} items)</SummaryLabel>
          <SummaryValue>{formatPrice(calculateGrandTotal())}</SummaryValue>
        </SummaryRow>
        
        <SummaryRow>
          <SummaryLabel>Tax (8%)</SummaryLabel>
          <SummaryValue>{formatPrice(calculateTax())}</SummaryValue>
        </SummaryRow>
        
        <SummaryRow>
          <SummaryLabel>Total</SummaryLabel>
          <SummaryValue $highlight>{formatPrice(calculateFinalTotal())}</SummaryValue>
        </SummaryRow>
      </OrderSummary>

      {/* Payment Method */}
      <PaymentSection>
        <SectionTitle>💳 Payment Method</SectionTitle>
        <PaymentOptions>
          <PaymentOption
            $selected={paymentMethod === 'card'}
            onClick={() => setPaymentMethod('card')}
          >
            💳 Credit/Debit Card
          </PaymentOption>
          <PaymentOption
            $selected={paymentMethod === 'paypal'}
            onClick={() => setPaymentMethod('paypal')}
          >
            🅿️ PayPal
          </PaymentOption>
          <PaymentOption
            $selected={paymentMethod === 'apple'}
            onClick={() => setPaymentMethod('apple')}
          >
            🍎 Apple Pay
          </PaymentOption>
        </PaymentOptions>
      </PaymentSection>

      {/* Action Buttons */}
      <ActionButtons>
        <CloseButton onClick={onClose}>
          Cancel
        </CloseButton>
        <ProcessButton onClick={handleProcessOrder}>
          🚀 Process Order - {formatPrice(calculateFinalTotal())}
        </ProcessButton>
      </ActionButtons>
    </CheckoutContainer>
  );
};

export default UnifiedCheckout;
